import * as React from 'react';
import Helmet from 'react-helmet';

import { withIntl, IntlProps } from 'ha/i18n';
import { notEmpty } from 'ha/utils/notEmpty';
import BreadcrumbItem from './BreadcrumbItem';
import Grid, { GridSpacing } from 'ha/ui/next/Grid';
interface BreadCrumb {
  name: string;
  schemaName?: string;
  path?: string;
  schemaPath?: string;
}

const DefaultSpacing = 8;

interface Props {
  breadcrumbs: Array<BreadCrumb | undefined>;
  withoutFirstSeparator?: boolean;
  withJsonLDSchema?: boolean;
  spacing?: GridSpacing;
}

const getJsonLdBreadcrumbs = (breadcrumbs: BreadCrumb[]) =>
  JSON.stringify({
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map(
      ({ name, schemaName = name, path, schemaPath = path }, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@id': schemaPath,
          name: schemaName,
        },
      }),
    ),
  });

const Breadcrumbs: React.SFC<Props & IntlProps> = ({
  breadcrumbs,
  withoutFirstSeparator = false,
  withJsonLDSchema = false,
  spacing,
  intl: { T, urlResolver },
}) => {
  const filteredBreadcrumbs = breadcrumbs.filter(notEmpty);

  const isMany = filteredBreadcrumbs.length > 1;

  const first = filteredBreadcrumbs[0];
  const last = filteredBreadcrumbs[filteredBreadcrumbs.length - 1];
  const rest = filteredBreadcrumbs.slice(1, -1);

  return (
    <Grid
      container
      wrap="nowrap"
      spacing={spacing || DefaultSpacing}
      data-test-locator="Breadcrumbs"
    >
      {withJsonLDSchema && (
        <Helmet>
          <script type="application/ld+json">
            {getJsonLdBreadcrumbs([
              { name: T('Home'), path: urlResolver.getBaseUrl() },
              ...filteredBreadcrumbs,
            ])}
          </script>
        </Helmet>
      )}
      <BreadcrumbItem
        {...first}
        renderAsLink={isMany}
        withoutSeparator={withoutFirstSeparator}
      />
      {rest.map(item => (
        <BreadcrumbItem {...item} renderAsLink key={item.name} />
      ))}
      {isMany && <BreadcrumbItem {...last} renderAsLink={false} />}
    </Grid>
  );
};

export default withIntl(Breadcrumbs);
